import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getSingleBillReturnUrl, SingleBillInitializer } from '@ctct/application-layer';
import { BillingStrategy, createResolvedBillingStrategy } from '@ctct/domain-layer';
import { envs } from '@ctct/infrastructure-layer';
import { Heading, PayflowTopBar } from '@ctct/presentation-layer';
import {
  isStripeParamEnabled,
  AlertWidget,
  SingleBillInfoFormWidget,
  SingleBillPaymentMethodsWidget,
  SubmitSingleBillWidget,
  useRouterNonUiWidget,
  useSingleBillPaymentMethodsWidget,
  useSubmitSingleBillWidget,
  useSingleBillInfoFormWidget,
  useAlertWidget,
  useSingleBillBillingCountry,
} from '@ctct/react-app-layer';
import {
  createUpdateSingleBillHooks,
  useSingleBillPageStore,
  createSingleBillPaymentMethodsHooks,
  createSingleBillInfoFormHooks,
  createAlertHooks,
} from '@ctct/state-client-layer';
import { useAllCountries, usePartner, usePaymentStrategy, useSingleBill } from '@ctct/state-server-layer';

import './single-bill-info-page.scss';

const linkToDashboard = `https://app${envs.getEnv()}constantcontact.com/home`;

const initializer = new SingleBillInitializer();
const updateSingleBillHooks = createUpdateSingleBillHooks(useSingleBillPageStore);
const singleBillPaymentMethodsHooks = createSingleBillPaymentMethodsHooks(useSingleBillPageStore);
const singleBillInfoFormHooks = createSingleBillInfoFormHooks(useSingleBillPageStore);
const alertHooks = createAlertHooks(useSingleBillPageStore);

const routerNonUiExternalHooks = {
  useIsHidePromoCodesEnabled: () => false, // no promo code application on this page
};

export const SingleBillPage = () => {
  const { t } = useTranslation();
  const isStripeEnabled = isStripeParamEnabled();
  const returnUrl = getSingleBillReturnUrl();
  const goBackUrl = returnUrl ?? linkToDashboard;
  const countriesQuery = useAllCountries();
  const partnerQuery = usePartner();
  const singleBillQuery = useSingleBill();

  const billingStrategy = createResolvedBillingStrategy(
    isStripeEnabled ? BillingStrategy.Stripe : BillingStrategy.InHouse
  );
  const paymentStrategy = usePaymentStrategy({
    billingStrategyQuery: billingStrategy,
  });

  const submitSingleBillWidgetProps = useSubmitSingleBillWidget({
    initializer,
    pageStore: useSingleBillPageStore,
    hooks: updateSingleBillHooks,
  });

  const singleBillPaymentMethodsWidgetProps = useSingleBillPaymentMethodsWidget({
    initializer,
    pageStore: useSingleBillPageStore,
    hooks: singleBillPaymentMethodsHooks,
    paymentStrategy,
    flags: {
      shouldIgnoreRatingCall: true,
    },
  });

  const singleBillInfoFormWidgetProps = useSingleBillInfoFormWidget({
    initializer,
    pageStore: useSingleBillPageStore,
    hooks: singleBillInfoFormHooks,
  });

  const alertWidgetProps = useAlertWidget({
    initializer,
    pageStore: useSingleBillPageStore,
    hooks: alertHooks,
  });
  const isStripeSingleBillEnabled = partnerQuery.data?.isPartnerProvisionAccountsToStripe() === true;

  useSingleBillBillingCountry({
    countries: countriesQuery.data,
    isStripeSingleBillEnabled,
    pageStore: useSingleBillPageStore,
    singleBill: singleBillQuery.data,
    singleBillInfoFormWidgetProps,
  });

  useRouterNonUiWidget({
    initializer,
    isStripeEnabled: false,
    isStripePhase2Enabled: false,
    externalHooks: routerNonUiExternalHooks,
  });

  useEffect(() => {
    initializer.listeners.on();

    return () => {
      initializer.listeners.off();
    };
  }, []);

  return (
    <>
      <PayflowTopBar exitLink={linkToDashboard} logoLink={linkToDashboard} />
      <a className="single-bill-page-go-back" href={goBackUrl}>
        ← Go Back
      </a>

      <div className="single-bill-page-container">
        <Heading subpager>{t('single-bill-page.heading')}</Heading>

        <AlertWidget {...alertWidgetProps} />

        <SingleBillInfoFormWidget {...singleBillInfoFormWidgetProps} />

        <SingleBillPaymentMethodsWidget {...singleBillPaymentMethodsWidgetProps} />

        <SubmitSingleBillWidget {...submitSingleBillWidgetProps} />
      </div>
    </>
  );
};
